import { Location } from '@angular/common';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  effect,
  inject,
  input,
  TemplateRef,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
  confirmActionDialog,
  exitConfirmationDialog,
} from '@sportello-balneari/shared-components';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PaginatorModule } from 'primeng/paginator';
import { TabsModule } from 'primeng/tabs';
import { TabListPassThrough } from 'primeng/types/tabs';
import { map } from 'rxjs';
import { CompilingFormService } from '../../services/compiling/compiling-form.service';
import { AnswerPermissionsService } from '../../services/compiling/state/answer-permissions.service';
import { BulkCompileStatusComponent } from './bulk-compile/bulk-compile-status.component';
import { CompilingDashboardService } from './compiling-dashboard.service';
import { ApplicantFormComponent } from './envelope-a/applicant-form.component';
import { EnvelopBComponent } from './envelope-b/envelope-b.component';
import { EnvelopeCComponent } from './envelope-c/envelope-c.component';
import { Result } from './result/result';
import { SummaryComponent } from './summary/summary.component';

/** The tab holding the compilable form questions (Offerta Tecnica). */
const TECHNICAL_OFFER_TAB = 2;
/** The host page's own tab (see `settingsTab`), after the results. */
const SETTINGS_TAB = 5;

@Component({
  selector: 'lib-compiling-dashboard',
  templateUrl: './compiling-dashboard.component.html',
  styleUrls: ['./compiling-dashboard.component.css'],
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatIconModule,
    ButtonModule,
    ToastModule,
    FormsModule,
    ConfirmDialogModule,
    ApplicantFormComponent,
    PaginatorModule,
    TabsModule,
    EnvelopBComponent,
    EnvelopeCComponent,
    SummaryComponent,
    Result,
    BulkCompileStatusComponent,
  ],
  host: {
    class: 'flex flex-1 w-full h-full',
  },
  // MessageService is the dashboard's own, so the outlet for it has to be here
  // too: the host pages render a toast bound to *their* instance, which never
  // sees anything raised inside the dashboard.
  providers: [MessageService, Location, CompilingDashboardService],
})
export class CompilingDashboardComponent {
  readonly mode = input<'full' | 'detached'>('full');

  /**
   * An optional last tab, *Impostazioni*, whose content the host page projects
   * as `<ng-template #settingsTab>` — a card per subsection (sharing, for one).
   * The dashboard does not know what goes in it, only where: after Risultati,
   * set apart by a divider, and only in full mode. No template, no tab.
   */
  readonly settingsTab = contentChild<TemplateRef<unknown>>('settingsTab');

  protected readonly forms = inject(CompilingFormService);
  protected readonly permissions = inject(AnswerPermissionsService);
  private readonly confirmService = inject(ConfirmationService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dashboard = inject(CompilingDashboardService);

  readonly selectedTab = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => Number(params.get('tab') ?? 0)),
    ),
    { initialValue: Number(this.route.snapshot.queryParamMap.get('tab') ?? 0) },
  );

  protected readonly hasSettingsTab = computed(
    () => this.mode() === 'full' && !!this.settingsTab(),
  );

  /** In detached mode, tab 0 (Summary), tab 4 (Results) and tab 5 (Settings)
   *  are hidden. Envelope A (Offerta Amministrativa, tab 1) IS shown —
   *  restricted to the candidate info only. If the stored/query-param value
   *  would land on a hidden tab, fall back to tab 1 (the first visible tab).
   *  A settings tab without a template (a stale URL, or a page that has none)
   *  falls back to the summary. */
  readonly effectiveTab = computed(() => {
    const tab = this.selectedTab();
    if (
      this.mode() === 'detached' &&
      (tab === 0 || tab === 4 || tab === SETTINGS_TAB)
    ) {
      return 1;
    }
    if (tab === SETTINGS_TAB && !this.hasSettingsTab()) {
      return 0;
    }
    return tab;
  });

  protected readonly SETTINGS_TAB = SETTINGS_TAB;

  constructor() {
    // Read-only technical offer: every question widget binds its FormControl,
    // so disabling the group is what greys out all of them at once. The other
    // sections and the buttons that would write (compile, upload, request
    // evaluation, PEF edits) check their own permission themselves.
    effect(() => {
      if (this.forms.ready() && !this.permissions.canEditTechnicalOffer()) {
        this.forms.form.disable();
      }
    });

    // A bulk-compile result can link to a question in a tab that is not the one
    // on screen, so honour the request before envelope B tries to scroll to it.
    effect(() => {
      const question = this.dashboard.questionToFocus();
      if (question && this.effectiveTab() !== TECHNICAL_OFFER_TAB) {
        untracked(() => this.onTabChange(TECHNICAL_OFFER_TAB));
      }
    });
  }

  onTabChange(tab: number | string | undefined) {
    if (tab == null) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    window.dispatchEvent(new Event('resize'));
  }

  readonly tabListPt: TabListPassThrough = {
    tabList: {
      style: 'justify-content: center!important;',
    },
  };

  discard() {
    confirmActionDialog(this.confirmService, {
      header: "Conferma l'operazione",
      message:
        'Tutte le modifiche apportate verranno salvate nella sezione "Bozze"',
      key: 'positionDialog',
      accept: async () => {
        this.location.back();
      },
    });
  }

  exit() {
    exitConfirmationDialog(this.confirmService, {
      key: 'positionDialog',
      accept: async () => {
        this.location.back();
      },
    });
  }
}
